import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uploadFile, type ChatAttachment } from '@/services/DataService';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';
import { UPLOADED_FILE_LIST_QUERY_KEYS } from '@/constants/library';

/**
 * Imperatively upload a single file to the tenant file store (`POST /file`).
 *
 * Returns a referentially-stable `(file: File) => Promise<ChatAttachment>` to
 * inject into the composer's `PromptInput`. The composer fires one call per
 * picked file and tracks per-file upload status itself, so we expose the raw
 * `mutateAsync` rather than the (single) mutation pending state.
 */
export const useUploadFile = (): ((file: File) => Promise<ChatAttachment>) => {
  const trackEvent = useTrack();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: (file: File) => uploadFile(file),
    onSuccess: (_result, file) => {
      trackEvent(ANALYTICS_EVENTS.CHAT_FILE_ATTACHED, {
        file_type: file.type || 'unknown',
        file_size_bytes: file.size,
        source: 'upload',
      });
      // The file is in the Library and the image picker now; neither may keep
      // showing a list from before it. Not awaited: the composer is not held
      // up for lists that are refetched only when next shown.
      UPLOADED_FILE_LIST_QUERY_KEYS.forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey });
      });
    },
  });
  return mutateAsync;
};
